import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Code2, Users, TrendingUp, Heart, Coffee, Lightbulb,
  Award, MapPin, Clock, IndianRupee, ChevronRight, X, Lock,
  Brain, Monitor, Layers, Zap, Shield, BookOpen, Rocket,
  CheckCircle, ArrowRight,
} from 'lucide-react';

/* ══════  DATA  ══════ */

const STATS = [
  { value: '3+',  label: 'Open Positions' },
  { value: '2+',  label: 'Offices in India' },
  { value: '500+', label: 'Projects Shipped' },
  { value: '100%', label: 'Remote Friendly' },
];

const VALUES = [
  { icon: Lightbulb, title: 'Innovation First',     desc: 'We embrace cutting-edge tech and encourage creative problem-solving at every level.' },
  { icon: Users,     title: 'Team Collaboration',   desc: 'Diverse perspectives and collaborative teamwork are how we achieve excellence.' },
  { icon: TrendingUp,title: 'Continuous Growth',    desc: "We invest in your development. Career growth isn't a perk — it's the plan." },
  { icon: Heart,     title: 'Work-Life Balance',    desc: 'A healthy environment where professional success and personal well-being coexist.' },
];

const BENEFITS = [
  { icon: IndianRupee, title: 'Competitive Salary',      desc: 'Industry-leading packages with performance bonuses and regular reviews.' },
  { icon: Rocket,      title: 'Career Growth',           desc: 'Clear advancement paths, mentorship, and opportunities to lead projects.' },
  { icon: BookOpen,    title: 'Learning & Dev',          desc: 'Training budgets, certifications, conference support, and premium platforms.' },
  { icon: Coffee,      title: 'Flexible Work',           desc: 'Hybrid options, flexible hours, and a comfortable office environment.' },
  { icon: Shield,      title: 'Health Benefits',         desc: 'Health insurance, wellness programs, and mental health support.' },
  { icon: Zap,         title: 'Real Impact',             desc: 'Work on products used by real businesses — see your code in production.' },
];

const JOBS = [
  {
    id: 1,
    title: 'Software Development Engineer — Full Stack',
    department: 'Engineering',
    location: 'Gorakhpur / Remote',
    type: 'Full-time',
    experience: '3–5 years',
    salary: '₹8–15 LPA',
    icon: Layers,
    badge: 'SDE',
    badgeColor: 'bg-blue-100 text-blue-700',
    skills: ['React.js', 'Node.js', 'TypeScript', 'MongoDB', 'AWS', 'REST APIs'],
    desc: 'Build scalable, high-performance web applications and enterprise solutions for clients across India and globally.',
    responsibilities: [
      'Design and develop full-stack web apps using React.js and Node.js',
      'Architect scalable backend services and RESTful APIs',
      'Collaborate with designers to implement user-centric interfaces',
      'Write clean, well-tested, documented code following best practices',
      'Participate in code reviews and drive technical decision-making',
      'Mentor junior developers and contribute to knowledge sharing',
    ],
    requirements: [
      '3+ years professional full-stack development experience',
      'Strong proficiency in React.js, Node.js, modern JavaScript (ES6+)',
      'Hands-on with MongoDB or PostgreSQL and AWS',
      'Solid understanding of REST APIs, auth flows, and microservices',
      'Excellent debugging, problem-solving, and communication skills',
      "Bachelor's in CS or equivalent practical experience",
    ],
  },
  {
    id: 2,
    title: 'Frontend Developer — Intern',
    department: 'Engineering',
    location: 'Gorakhpur (On-site / Hybrid)',
    type: 'Internship · 6 Months',
    experience: 'Fresher / 0–1 year',
    salary: '₹5,000–12,000 / month',
    icon: Monitor,
    badge: 'Intern',
    badgeColor: 'bg-green-100 text-green-700',
    skills: ['HTML', 'CSS', 'JavaScript', 'React.js', 'Tailwind CSS', 'Git'],
    desc: 'A hands-on internship for ambitious students and fresh graduates who want real-world frontend development exposure.',
    responsibilities: [
      'Build responsive UI components using React.js and Tailwind CSS',
      'Convert Figma mockups into pixel-perfect web interfaces',
      'Collaborate with senior devs to integrate frontend with backend APIs',
      'Participate in daily standups and sprint planning',
      'Write clean HTML/CSS/JS with attention to performance',
      'Fix cross-browser and mobile compatibility issues',
    ],
    requirements: [
      'Pursuing or completed degree in CS, IT, or related field',
      'Basic knowledge of HTML, CSS, JavaScript fundamentals',
      'Familiarity with React.js or strong willingness to learn',
      'Understanding of responsive design and Git workflow',
      'Eagerness to learn and work collaboratively in a team',
      'Portfolio or personal projects are a strong plus',
    ],
  },
  {
    id: 3,
    title: 'Senior Machine Learning Engineer',
    department: 'AI / ML',
    location: 'Remote / Gorakhpur',
    type: 'Full-time',
    experience: '5+ years',
    salary: '₹20–35 LPA',
    icon: Brain,
    badge: 'Senior',
    badgeColor: 'bg-purple-100 text-purple-700',
    skills: ['Python', 'TensorFlow', 'PyTorch', 'NLP', 'MLOps', 'AWS SageMaker', 'SQL'],
    desc: 'Lead AI/ML systems that power our intelligent software solutions — from research and prototyping to production.',
    responsibilities: [
      'Design, build, and deploy production-grade ML models and pipelines',
      'Lead ML architecture decisions and establish team best practices',
      'Develop NLP, computer vision, or predictive analytics solutions',
      'Collaborate with engineers to integrate ML models into products',
      'Monitor model performance and handle data drift',
      'Mentor mid-level engineers and drive team capability',
    ],
    requirements: [
      '5+ years professional ML/AI engineering experience',
      'Deep expertise in Python, TensorFlow, PyTorch, Scikit-learn',
      'MLOps: model versioning, CI/CD for ML, monitoring',
      'Cloud ML platforms — AWS SageMaker or GCP AI Platform',
      'Solid statistics, linear algebra, and ML fundamentals',
      "Master's or PhD in CS, Data Science, or related field preferred",
    ],
  },
];

const PROCESS = [
  { step: '01', title: 'Apply Online',     desc: 'Submit your resume via the contact page. No lengthy forms.' },
  { step: '02', title: 'HR Screening',     desc: 'A 20-min call to understand your background and goals.' },
  { step: '03', title: 'Technical Round',  desc: 'A practical task or technical interview with our engineering lead.' },
  { step: '04', title: 'Final Offer',      desc: 'Offer letter, onboarding plan, and your start date — usually within a week.' },
];

/* ══════  ANIMATION  ══════ */

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

/* ══════  JOB CARD  ══════ */

function JobCard({ job, onClosedClick }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = job.icon;

  return (
    <motion.div variants={fadeUp}
      className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Header row */}
      <div className="p-6 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gray-50 border border-gray-100 rounded-2xl flex items-center justify-center text-gray-500 flex-shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-base font-bold text-gray-900 leading-snug">{job.title}</h3>
              <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${job.badgeColor}`}>
                {job.badge}
              </span>
            </div>
            <p className="text-gray-500 text-sm mb-3 leading-relaxed">{job.desc}</p>
            <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.location}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {job.type}</span>
              <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5" /> {job.experience}</span>
              <span className="flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> {job.salary}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {job.skills.map(s => (
                <span key={s} className="bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full text-[11px] font-medium">{s}</span>
              ))}
            </div>
          </div>
          <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.22 }} className="flex-shrink-0 mt-1">
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </motion.div>
        </div>
      </div>

      {/* Expanded details */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden">
            <div className="border-t border-gray-50 bg-gray-50/50 p-6">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-blue-500" /> Key Responsibilities
                  </h4>
                  <ul className="space-y-2">
                    {job.responsibilities.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />{r}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-green-500" /> Requirements
                  </h4>
                  <ul className="space-y-2">
                    {job.requirements.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" />{r}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={onClosedClick}
                  className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2">
                  Apply Now <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => window.location.href = '/contact'}
                  className="border border-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold text-sm hover:bg-gray-100 transition-colors">
                  Ask a Question
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ══════  MAIN PAGE  ══════ */

const CareersPage = () => {
  const navigate = useNavigate();
  const [closedModal, setClosedModal] = useState(false);

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Careers at Vibhraad Technology | Software & AI Jobs India</title>
        <meta name="description" content="Join Vibhraad Technology — we're hiring Full Stack SDE, Frontend Intern, and Senior ML Engineer. Build real products with a growing tech company based in India." />
      </Helmet>

      {/* ── Applications Closed Modal ── */}
      <AnimatePresence>
        {closedModal && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center px-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setClosedModal(false)} />
            <motion.div className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center z-10"
              initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}>
              <button onClick={() => setClosedModal(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                <X className="w-4 h-4 text-gray-600" />
              </button>
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Applications Paused</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Online applications are currently closed. Follow us on LinkedIn or send your resume directly to vibhraad@gmail.com — we'll reach out when a match opens up.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setClosedModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors">
                  Got it
                </button>
                <button onClick={() => { setClosedModal(false); navigate('/contact'); }}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-500 transition-colors">
                  Email Us
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Hero ── */}
      <section className="relative bg-gray-950 overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-700/20 blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-indigo-600/15 blur-[100px]" />
          {/* Dot grid */}
          <div className="absolute inset-0 opacity-[0.04]"
            style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
        </div>

        <div className="relative max-w-5xl mx-auto px-5 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75 }}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-500/25 text-blue-400 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-8">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              We're Hiring · 3 Open Positions
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              Build the{' '}
              <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Future</span>
              <br />With Us
            </h1>

            <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
              Join Vibhraad Technology and work on real-world products that matter.
              Grow fast, ship great software, and do the best work of your career.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => document.getElementById('openings').scrollIntoView({ behavior: 'smooth' })}
                className="group bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-bold text-sm transition-all shadow-lg shadow-blue-600/30 inline-flex items-center justify-center gap-2">
                View Open Positions <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button onClick={() => navigate('/contact')}
                className="border border-white/15 hover:border-white/30 text-white/80 hover:text-white px-8 py-4 rounded-2xl font-semibold text-sm transition-all">
                Contact HR Team
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats strip ── */}
      <div className="bg-blue-600 py-8">
        <div className="max-w-4xl mx-auto px-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {STATS.map(({ value, label }) => (
              <div key={label}>
                <div className="text-3xl font-black text-white">{value}</div>
                <div className="text-blue-100 text-xs font-medium mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Values ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-5">
          <motion.div className="text-center mb-14"
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full mb-3">Our Culture</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Core Values</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
              We build an environment where talented people can do meaningful work and grow fast.
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {VALUES.map(({ icon: Icon, title, desc }) => (
              <motion.div key={title} variants={fadeUp}
                className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mx-auto mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5">
          <motion.div className="text-center mb-14"
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full mb-3">Perks & Benefits</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Why Work With Us</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
              Competitive pay, growth opportunities, and a culture where your ideas actually matter.
            </p>
          </motion.div>

          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <motion.div key={title} variants={fadeUp}
                className="flex items-start gap-4 p-6 rounded-2xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all duration-300 group">
                <div className="w-10 h-10 bg-blue-50 group-hover:bg-blue-600 rounded-xl flex items-center justify-center text-blue-600 group-hover:text-white flex-shrink-0 transition-colors">
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Hiring Process ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5">
          <motion.div className="text-center mb-14"
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full mb-3">How to Join</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Our Hiring Process</h2>
            <p className="text-gray-500 text-sm">Simple, fast, and transparent. Most offers go out within 2 weeks.</p>
          </motion.div>

          <div className="relative grid md:grid-cols-4 gap-8">
            {/* Connector line */}
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent" />
            {PROCESS.map(({ step, title, desc }) => (
              <motion.div key={step} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="text-center relative z-10">
                <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-600/20">
                  <span className="text-white font-black">{step}</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-1.5 text-sm">{title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Job Openings ── */}
      <section id="openings" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-5">
          <motion.div className="text-center mb-14"
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full mb-3">Open Roles</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Current Openings</h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              3 open positions across engineering and AI. Click any role to see full details and requirements.
            </p>
          </motion.div>

          <motion.div className="space-y-4"
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {JOBS.map(job => (
              <JobCard key={job.id} job={job} onClosedClick={() => setClosedModal(true)} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-gray-950 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[200px] bg-blue-700/15 blur-[80px] rounded-full pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-5 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
              Don't See the <span className="text-blue-400">Right Role?</span>
            </h2>
            <p className="text-gray-400 text-base mb-10 max-w-xl mx-auto leading-relaxed">
              We're always looking for talented people. Send us your resume — we'll reach out when the perfect opportunity opens.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => navigate('/contact')}
                className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-bold text-sm transition-all shadow-lg shadow-blue-600/30 inline-flex items-center justify-center gap-2">
                Send Your Resume <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => navigate('/about')}
                className="border border-white/15 hover:border-white/30 text-white/80 hover:text-white px-8 py-4 rounded-2xl font-semibold text-sm transition-all">
                Learn About Us
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
